import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const TechnicalSupport = ({ isOpen, onClose }) => {
  const [issueType, setIssueType] = useState('');
  const [description, setDescription] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const issueTypes = [
    { value: 'audio', label: 'Audio Issues' },
    { value: 'video', label: 'Video Issues' },
    { value: 'connection', label: 'Connection Problems' },
    { value: 'screen-share', label: 'Screen Sharing Issues' },
    { value: 'other', label: 'Other Technical Issue' },
  ];

  const commonSolutions = [
    {
      issue: 'No audio',
      solution: 'Check microphone permissions in browser settings and ensure correct device is selected',
      icon: 'Mic',
    },
    {
      issue: 'Poor video quality',
      solution: 'Reduce video quality in settings or check internet connection speed',
      icon: 'Video',
    },
    {
      issue: 'Connection drops',
      solution: 'Switch to phone fallback option or move closer to WiFi router',
      icon: 'Wifi',
    },
    {
      issue: 'Echo or feedback',
      solution: 'Use headphones or enable echo cancellation in audio settings',
      icon: 'Volume2',
    },
  ];

  const handleSubmit = (e) => {
    e?.preventDefault();
    console.log('Support request:', { issueType, description });
    setSubmitted(true);
    setTimeout(() => {
      setSubmitted(false);
      onClose();
    }, 3000);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="bg-card rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <div className="flex items-center gap-3">
            <Icon name="HelpCircle" size={24} className="text-primary" />
            <h2 className="text-xl font-semibold text-foreground">Technical Support</h2>
          </div>
          <Button
            variant="ghost"
            size="icon"
            iconName="X"
            onClick={onClose}
          />
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {submitted ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <div className="w-16 h-16 rounded-full bg-success/10 flex items-center justify-center mb-4">
                <Icon name="CheckCircle" size={32} className="text-success" />
              </div>
              <h3 className="text-lg font-semibold text-foreground mb-2">
                Support Request Submitted
              </h3>
              <p className="text-sm text-muted-foreground max-w-md">
                Our technical team has been notified and will assist you shortly. You can continue your session while we investigate.
              </p>
            </div>
          ) : (
            <>
              {/* Common Solutions */}
              <div className="mb-6">
                <h3 className="text-base font-semibold text-foreground mb-4">
                  Common Solutions
                </h3>
                <div className="space-y-3">
                  {commonSolutions?.map((item, index) => (
                    <div
                      key={index}
                      className="p-4 bg-muted rounded-lg hover:bg-muted/80 transition-colors"
                    >
                      <div className="flex items-start gap-3">
                        <Icon name={item?.icon} size={20} className="text-primary mt-0.5" />
                        <div>
                          <p className="text-sm font-medium text-foreground mb-1">
                            {item?.issue}
                          </p>
                          <p className="text-sm text-muted-foreground">
                            {item?.solution}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Support Form */}
              <div className="pt-6 border-t border-border">
                <h3 className="text-base font-semibold text-foreground mb-4">
                  Still Need Help?
                </h3>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <Select
                    label="Issue type"
                    placeholder="Select issue type"
                    options={issueTypes}
                    value={issueType}
                    onChange={setIssueType}
                    required
                  />
                  <Input
                    label="Describe the issue"
                    type="text"
                    placeholder="What problem are you experiencing?"
                    value={description}
                    onChange={(e) => setDescription(e?.target?.value)}
                    description="Include any error messages or specific symptoms"
                    required
                  />
                  <div className="bg-muted rounded-lg p-4">
                    <div className="flex items-start gap-3">
                      <Icon name="Info" size={20} className="text-primary mt-0.5" />
                      <div className="text-sm text-muted-foreground">
                        <p className="font-medium text-foreground mb-1">
                          Emergency Support
                        </p>
                        <p>
                          For urgent technical issues affecting session safety, call our 24/7 support line at{' '}
                          <span className="text-primary font-medium">1-800-TECH-HELP</span>
                        </p>
                      </div>
                    </div>
                  </div>
                  <Button
                    type="submit"
                    variant="default"
                    iconName="Send"
                    iconPosition="right"
                    fullWidth
                    disabled={!issueType || !description}
                  >
                    Submit Support Request
                  </Button>
                </form>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default TechnicalSupport;